import { useCurrentUser } from '@shared/stores/current-user'
import { $t } from 'src/plugins/i18n'
import type { ProxyRecord } from 'src/services/deserializers/deserializers.types'
import { PRODUCT_KIND } from 'src/services/mappings/portfolio/portfolio.const'
import { PORTFOLIO_AMOUNT_LABEL } from 'src/services/mappings/portfolio/portfolio.utils.const'
import { FEATURE_IDS } from 'src/tpbp-core/feature-flag/featureFlags.const'
import { hasFeature } from 'src/tpbp-core/feature-flag/featureFlags.utils'
import { ProviderName } from 'src/tpbp-core/models/common.const'
import {
  type PortfolioConfigurationModel,
  type PortfolioModel,
} from 'src/tpbp-json-api-handler/models/models'
import { groupBy } from 'underscore'

export function isLifeInsuranceOrCapitalization(productKind: string): boolean {
  return (
    productKind === PRODUCT_KIND.lifeInsurance ||
    productKind === PRODUCT_KIND.bonsEtContratsDeCapitalisation
  )
}

export function portfolioAmountFieldLabel(record) {
  const { productKind } = record
  const altLabel = Object.hasOwn(PORTFOLIO_AMOUNT_LABEL, productKind)
    ? PORTFOLIO_AMOUNT_LABEL[productKind]
    : ''

  return altLabel
    ? $t(`altLabels.portfolios.${altLabel}`)
    : $t('models.portfolios.fields.amount')
}

export function portfolioHaveComplementaryPayment(portfolio): boolean {
  return portfolio.amount > portfolio.amount_initial
}

export function portfolioHaveRegularPayment(portfolio): boolean {
  return (
    portfolio.monthly_payment_initial === 0 && portfolio.monthly_payment > 0
  )
}

export function portfolioHaveMoreRegularPayment(portfolio): boolean {
  return (
    portfolio.monthly_payment_initial > 0 &&
    portfolio.monthly_payment > portfolio.monthly_payment_initial
  )
}

export function portfolioHaveLessRegularPayment(portfolio): boolean {
  return (
    portfolio.monthly_payment_initial > 0 &&
    portfolio.monthly_payment < portfolio.monthly_payment_initial
  )
}

export function portfolioHaveNoMoreRegularPayment(portfolio): boolean {
  return (
    portfolio.monthly_payment_initial > 0 && portfolio.monthly_payment === 0
  )
}

export function getProductCategoryByProductKind(
  portfolioConfiguration: PortfolioConfigurationModel[],
) {
  return groupBy(portfolioConfiguration, 'productCategory')
}

export function getProductGroupByProductKind(
  portfolioConfiguration: PortfolioConfigurationModel[],
) {
  return groupBy(portfolioConfiguration, 'productGroup')
}

export function getProductGroupMacroByProductKind(
  portfolioConfiguration: PortfolioConfigurationModel[],
) {
  return groupBy(portfolioConfiguration, 'productGroupMacro')
}

export function isProductKindInProductCategory(
  productKind: string,
  productCategory: string,
  allProductCategories: ReturnType<typeof getProductCategoryByProductKind>,
) {
  return allProductCategories[productCategory]?.some(
    (config) => config.productKind === productKind,
  )
}

export function isProductKindInProductGroup(
  productKind: string,
  productGroup: string,
  allProductGroups: ReturnType<typeof getProductGroupByProductKind>,
) {
  return allProductGroups[productGroup]?.some(
    (config) => config.productKind === productKind,
  )
}

export function isProductKindInProductGroupMacro(
  productKind: string,
  productGroupMacro: string,
  allProductGroupsMacro: ReturnType<typeof getProductGroupMacroByProductKind>,
) {
  return allProductGroupsMacro[productGroupMacro]?.some(
    (config) => config.productKind === productKind,
  )
}

export function getPortfolioIsDeletable(portfolio: ProxyRecord) {
  const { $meta, isInternal, providerName } = portfolio as PortfolioModel
  const { isAdvisor } = useCurrentUser()

  // if Advisor and provider oxlin, dont allow delete
  if (isAdvisor.value && providerName === ProviderName.linxo) {
    return false
  }

  if (hasFeature(FEATURE_IDS.usePortfolioMeta)) {
    return $meta?.is_deletable === true
  }

  return !isInternal && providerName !== ProviderName.linxo
}
